import {
  FileText, Link2, Phone, MessageSquare, Mail, MessageCircle, Video, MapPin, Wifi,
  Calendar, Coins, User, Banknote,
} from 'lucide-react';

/**
 * QR content-type definitions and the plain-string payload each one encodes
 * into the QR code itself. Formats (tel:, SMSTO:, MATMSG:, WIFI:, vCard,
 * geo:, wa.me, facetime:, coin URIs, VEVENT) are the same ones phones
 * already know how to read from a scanned QR. Icon + tooltip are shared
 * between the logged-in QR Builder and the public generator so the type
 * picker looks and reads identically in both places.
 */
export const QR_TYPES = [
  { value: 'text', label: 'Text', icon: FileText, tooltip: 'Show people a block of text by scanning the QR code.' },
  { value: 'url', label: 'URL', icon: Link2, tooltip: 'Send someone to a link.' },
  { value: 'phone', label: 'Phone', icon: Phone, tooltip: 'Get calls from people by scanning the QR code.' },
  { value: 'sms', label: 'SMS', icon: MessageSquare, tooltip: 'Get custom SMS messages from people by scanning the QR code.' },
  { value: 'email', label: 'Email', icon: Mail, tooltip: 'Get custom Emails from people by scanning the QR code.' },
  { value: 'whatsapp', label: 'Whatsapp', icon: MessageCircle, tooltip: 'Get custom Whatsapp messages from people by scanning the QR code.' },
  { value: 'facetime', label: 'Facetime', icon: Video, tooltip: 'Get Facetime calls from people by scanning the QR code.' },
  { value: 'location', label: 'Location', icon: MapPin, tooltip: 'Embed location coordinates for people to get directions more easily.' },
  { value: 'wifi', label: 'WiFi', icon: Wifi, tooltip: 'Create WiFi QR codes and let people connect to your WiFi more easily.' },
  { value: 'event', label: 'Event', icon: Calendar, tooltip: 'Generate a custom calendar event and embed it into a QR code.' },
  { value: 'crypto', label: 'Crypto', icon: Coins, tooltip: 'Get paid in crypto from people by scanning the QR code.' },
  { value: 'vcard', label: 'Vcard', icon: User, tooltip: 'Embed all your contact data into a digital business card.' },
  { value: 'paypal', label: 'PayPal', icon: Banknote, tooltip: 'Create PayPal direct payment links.' },
];

export const CRYPTO_COINS = [
  { value: 'bitcoin', label: 'Bitcoin (BTC)' },
  { value: 'ethereum', label: 'Ethereum (ETH)' },
  { value: 'litecoin', label: 'Litecoin (LTC)' },
  { value: 'dogecoin', label: 'Dogecoin (DOGE)' },
  { value: 'bitcoincash', label: 'Bitcoin Cash (BCH)' },
];

export const PAYPAL_TYPES = [
  { value: 'buy_now', label: 'Buy Now', cmd: '_xclick' },
  { value: 'add_to_cart', label: 'Add to Cart', cmd: '_cart' },
];

export const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];

export function defaultContentFor(type) {
  switch (type) {
    case 'url':
      return { url: '' };
    case 'text':
      return { text: '' };
    case 'phone':
      return { phone: '' };
    case 'sms':
      return { phone: '', body: '' };
    case 'email':
      return { email: '', subject: '', body: '' };
    case 'whatsapp':
      return { phone: '', body: '' };
    case 'facetime':
      return { facetime: '' };
    case 'location':
      return { latitude: '', longitude: '' };
    case 'wifi':
      return { ssid: '', password: '', encryption: 'WPA', hidden: false };
    case 'event':
      return {
        event: '', event_location: '', event_url: '', event_note: '',
        event_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        event_start_datetime: '', event_end_datetime: '',
      };
    case 'crypto':
      return { crypto_coin: CRYPTO_COINS[0].value, crypto_address: '', crypto_amount: '' };
    case 'vcard':
      return {
        first_name: '', last_name: '', email: '', website: '', company: '', job_title: '',
        birthday: '', street: '', city: '', zip: '', region: '', country: '', note: '',
        phone_numbers: [''],
        social_label: [''], social_value: [''],
      };
    case 'paypal':
      return {
        paypal_type: PAYPAL_TYPES[0].value, paypal_email: '', paypal_title: '',
        paypal_price: '', paypal_currency: CURRENCIES[0],
        paypal_thank_you_url: '', paypal_cancel_url: '',
      };
    default:
      return {};
  }
}

function escapeVCardValue(value) {
  return String(value || '').replace(/([,;\\])/g, '\\$1');
}

// "YYYY-MM-DDTHH:mm" (datetime-local's value) -> "YYYYMMDDTHHmmss" (iCal local time, no Z - paired with TZID)
function toICalLocal(datetimeLocalValue) {
  if (!datetimeLocalValue) return '';
  const digits = datetimeLocalValue.replace(/[^0-9]/g, '');
  return digits.length >= 14 ? digits.slice(0, 8) + 'T' + digits.slice(8, 14) : digits + 'T000000';
}

export function buildQrData(type, content) {
  switch (type) {
    case 'url':
      return (content.url || '').trim();

    case 'text':
      return content.text || '';

    case 'phone':
      return `tel:${(content.phone || '').trim()}`;

    case 'sms':
      return `SMSTO:${content.phone || ''}:${content.body || ''}`;

    case 'email':
      return `MATMSG:TO:${content.email || ''};SUB:${content.subject || ''};BODY:${content.body || ''};;`;

    case 'whatsapp':
      return `https://wa.me/${(content.phone || '').replace(/[^0-9]/g, '')}${content.body ? `?text=${encodeURIComponent(content.body)}` : ''}`;

    case 'facetime':
      return `facetime:${(content.facetime || '').trim()}`;

    case 'location': {
      const lat = parseFloat(content.latitude) || 0;
      const lng = parseFloat(content.longitude) || 0;
      return `geo:${lat},${lng}?q=${lat},${lng}`;
    }

    case 'wifi': {
      const encryption = content.encryption === 'nopass' ? 'nopass' : content.encryption || 'WPA';
      let data = `WIFI:S:${escapeVCardValue(content.ssid)};T:${encryption};`;
      if (encryption !== 'nopass' && content.password) data += `P:${escapeVCardValue(content.password)};`;
      if (content.hidden) data += 'H:true;';
      return data + ';';
    }

    case 'event': {
      const lines = ['BEGIN:VEVENT'];
      lines.push(`SUMMARY:${content.event || ''}`);
      if (content.event_location) lines.push(`LOCATION:${content.event_location}`);
      if (content.event_url) lines.push(`URL:${content.event_url}`);
      if (content.event_note) lines.push(`DESCRIPTION:${content.event_note}`);
      const tz = content.event_timezone || 'UTC';
      if (content.event_start_datetime) lines.push(`DTSTART;TZID=${tz}:${toICalLocal(content.event_start_datetime)}`);
      if (content.event_end_datetime) lines.push(`DTEND;TZID=${tz}:${toICalLocal(content.event_end_datetime)}`);
      lines.push('END:VEVENT');
      return lines.join('\n');
    }

    case 'crypto': {
      const coin = content.crypto_coin || CRYPTO_COINS[0].value;
      const amount = content.crypto_amount ? `?amount=${content.crypto_amount}` : '';
      return `${coin}:${content.crypto_address || ''}${amount}`;
    }

    case 'vcard': {
      const esc = escapeVCardValue;
      const lines = ['BEGIN:VCARD', 'VERSION:3.0'];
      lines.push(`N:${esc(content.last_name)};${esc(content.first_name)};;;`);
      lines.push(`FN:${esc(`${content.first_name || ''} ${content.last_name || ''}`.trim())}`);
      if (content.company) lines.push(`ORG:${esc(content.company)}`);
      if (content.job_title) lines.push(`TITLE:${esc(content.job_title)}`);
      (content.phone_numbers || []).forEach((p) => {
        if (p && p.trim()) lines.push(`TEL;TYPE=CELL:${esc(p)}`);
      });
      if (content.email) lines.push(`EMAIL:${esc(content.email)}`);
      if (content.website) lines.push(`URL:${esc(content.website)}`);
      if (content.birthday) lines.push(`BDAY:${content.birthday.replace(/-/g, '')}`);
      if (content.street || content.city || content.region || content.zip || content.country) {
        lines.push(`ADR:;;${esc(content.street)};${esc(content.city)};${esc(content.region)};${esc(content.zip)};${esc(content.country)}`);
      }
      const socialLabels = content.social_label || [];
      const socialValues = content.social_value || [];
      socialLabels.forEach((label, i) => {
        const value = socialValues[i];
        if (value && value.trim()) lines.push(`URL;TYPE=${esc(label || 'social')}:${esc(value)}`);
      });
      if (content.note) lines.push(`NOTE:${esc(content.note)}`);
      lines.push('END:VCARD');
      return lines.join('\n');
    }

    case 'paypal': {
      const typeInfo = PAYPAL_TYPES.find((t) => t.value === content.paypal_type) || PAYPAL_TYPES[0];
      const params = new URLSearchParams({
        business: content.paypal_email || '',
        cmd: typeInfo.cmd,
        currency_code: content.paypal_currency || CURRENCIES[0],
        amount: content.paypal_price || '0',
        item_name: content.paypal_title || '',
      });
      if (typeInfo.value === 'add_to_cart') {
        params.set('button_subtype', 'products');
        params.set('add', '1');
      }
      if (content.paypal_thank_you_url) params.set('return', content.paypal_thank_you_url);
      if (content.paypal_cancel_url) params.set('cancel_return', content.paypal_cancel_url);
      return `https://www.paypal.com/cgi-bin/webscr?${params.toString()}`;
    }

    default:
      return '';
  }
}

export const DOT_STYLES = [
  { value: 'square', label: 'Square' },
  { value: 'dots', label: 'Dots' },
  { value: 'rounded', label: 'Rounded' },
  { value: 'classy', label: 'Classy' },
  { value: 'classy-rounded', label: 'Classy Rounded' },
  { value: 'extra-rounded', label: 'Extra Rounded' },
];

export const CORNER_SQUARE_STYLES = [
  { value: 'square', label: 'Square' },
  { value: 'dot', label: 'Dot' },
  { value: 'extra-rounded', label: 'Rounded' },
];

export const CORNER_DOT_STYLES = [
  { value: 'square', label: 'Square' },
  { value: 'dot', label: 'Dot' },
];

export const GRADIENT_STYLES = [
  { value: 'horizontal', label: 'Horizontal', rotation: 0 },
  { value: 'vertical', label: 'Vertical', rotation: Math.PI / 2 },
  { value: 'diagonal', label: 'Diagonal', rotation: Math.PI / 4 },
  { value: 'inverse_diagonal', label: 'Inverse Diagonal', rotation: -Math.PI / 4 },
  { value: 'radial', label: 'Radial', rotation: 0 },
];

export const ERROR_CORRECTION_LEVELS = [
  { value: 'L', label: 'Low (7%)' },
  { value: 'M', label: 'Medium (15%)' },
  { value: 'Q', label: 'Quartile (25%)' },
  { value: 'H', label: 'High (30%)' },
];

const SHORT_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';

/**
 * Proposed client-side so the live preview can show the real short link
 * before the first save - the backend re-checks uniqueness and only
 * regenerates on the rare collision.
 */
export function generateShortCode(length = 7) {
  const bytes = new Uint8Array(length);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  return Array.from(bytes, (b) => SHORT_CODE_ALPHABET[b % SHORT_CODE_ALPHABET.length]).join('');
}

export function shortLinkUrl(shortCode) {
  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${base}/q/${shortCode}`;
}

export function hexToRgba(hex, alphaPercent) {
  const clean = (hex || '#ffffff').replace('#', '');
  const expanded = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const bigint = parseInt(expanded, 16) || 0xffffff;
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  const alpha = Math.max(0, Math.min(100, alphaPercent)) / 100;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const DEFAULT_QR_SETTINGS = {
  dotStyle: 'square',
  cornerSquareStyle: 'square',
  cornerDotStyle: 'square',
  foregroundType: 'color', // 'color' | 'gradient'
  fgColor: '#000000',
  gradientStyle: 'horizontal',
  gradientColor1: '#000000',
  gradientColor2: '#000000',
  bgColor: '#ffffff',
  bgTransparency: 0,
  customEyesColor: false,
  eyesInnerColor: '#000000',
  eyesOuterColor: '#000000',
  logoUrl: null,
  logoSize: 25,
  size: 400,
  margin: 10,
  ecc: 'M',
};

export const DOWNLOAD_FORMATS = ['svg', 'png', 'jpeg', 'webp'];

/**
 * Turns our flat settings object into the qr-code-styling options shape.
 * Shared by the admin QR Builder and the public generator so a QR looks
 * identical regardless of which one built it - including the brittle bit
 * where imageOptions must always be a real object, even with no logo, or
 * the library throws reading hideBackgroundDots off undefined.
 */
export function buildQrStyleOptions(data, settings) {
  const gradient = settings.foregroundType === 'gradient' ? {
    type: settings.gradientStyle === 'radial' ? 'radial' : 'linear',
    rotation: GRADIENT_STYLES.find((g) => g.value === settings.gradientStyle)?.rotation || 0,
    colorStops: [
      { offset: 0, color: settings.gradientColor1 },
      { offset: 1, color: settings.gradientColor2 },
    ],
  } : undefined;

  const dotsOptions = gradient
    ? { type: settings.dotStyle, gradient }
    : { type: settings.dotStyle, color: settings.fgColor };

  const eyeColor = settings.customEyesColor ? settings.eyesOuterColor : settings.fgColor;
  const eyeDotColor = settings.customEyesColor ? settings.eyesInnerColor : settings.fgColor;
  const useGradientForEyes = gradient && !settings.customEyesColor;

  return {
    data,
    margin: settings.margin,
    qrOptions: { errorCorrectionLevel: settings.ecc },
    dotsOptions,
    cornersSquareOptions: useGradientForEyes
      ? { type: settings.cornerSquareStyle, gradient }
      : { type: settings.cornerSquareStyle, color: eyeColor },
    cornersDotOptions: useGradientForEyes
      ? { type: settings.cornerDotStyle, gradient }
      : { type: settings.cornerDotStyle, color: eyeDotColor },
    backgroundOptions: { color: hexToRgba(settings.bgColor, 100 - settings.bgTransparency) },
    image: settings.logoUrl || undefined,
    imageOptions: {
      imageSize: (settings.logoSize || 25) / 100,
      margin: 4,
      hideBackgroundDots: true,
      crossOrigin: 'anonymous',
    },
  };
}

const PENDING_DRAFT_KEY = 'qrBuilderPendingDraft';

/**
 * Carries a QR someone built on the public, no-login generator across to
 * the logged-in QR Builder, so clicking "Sign in to save it" doesn't
 * actually lose their work. Saved as {type, content, settings} right
 * before navigating to /login or /register.
 *
 * Two readers, deliberately not merged into one:
 * - peekPendingQrDraft: non-destructive existence check, used by
 *   Login/Register/VerifyEmail only to decide WHERE to send someone
 *   (QR Builder vs. the normal landing page). They are way-stations, not
 *   the final consumer, and must not clear the draft before it arrives.
 * - takePendingQrDraft: reads and clears, used exactly once by
 *   QrCodeCreate.jsx on landing to actually apply it.
 */
export function savePendingQrDraft(draft) {
  if (!draft) return;
  try { window.localStorage.setItem(PENDING_DRAFT_KEY, JSON.stringify(draft)); } catch (e) { /* storage unavailable - draft just won't carry over */ }
}

export function peekPendingQrDraft() {
  try {
    const raw = window.localStorage.getItem(PENDING_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function takePendingQrDraft() {
  const draft = peekPendingQrDraft();
  try { window.localStorage.removeItem(PENDING_DRAFT_KEY); } catch (e) { /* ignore */ }
  return draft;
}
